import {
  type PageSection,
  type SidebarItem,
  type SidebarItems,
  sidebarItemsSchema,
} from '@crowdex/plugin-contracts';
import { useEffect, useSyncExternalStore } from 'react';
import { callApp } from '@/features/apps/apps-client';
import { type PluginPanel, railPanels } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import {
  useDesktopShellStore,
  useOptionalDesktopShellStoreApi,
} from '@/lib/desktop-shell/store-context';
import { activePanelTab } from '@/lib/side-panel/panel-tabs';

/**
 * An app's navigation, which the core draws in the sidebar while the app's tab is the one shown
 * in the side panel (apps never draw a sidebar of their own, docs/redesign-implementation-plan.md
 * §6.1): its sections (`page.sidebars`), whose routes list their items in the open conversation's
 * workspace, read again when its view says they changed (`page.refresh`, `page.select`); and the
 * live sections its view declares for the document it shows (`page.sections.set`), per tab.
 */
type SidebarState = { items: readonly SidebarItem[]; loading: boolean; error: string | null };

const EMPTY: SidebarState = { items: [], loading: true, error: null };
const REFRESH_EVENT = 'crowdex:app-panel-sidebar-refresh';

const sidebars = new Map<string, SidebarState>();
const loads = new Map<string, number>();
const listeners = new Set<() => void>();

function publish(key: string, state: SidebarState) {
  sidebars.set(key, state);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The sections of an app's navigation (`page.sidebars`), none for a tool of the core. */
const NO_APP_SECTIONS: readonly AppSection[] = [];

export function appSections(panel: Pick<PluginPanel, 'rail'> | null): readonly AppSection[] {
  return panel?.rail?.sidebars ?? NO_APP_SECTIONS;
}
export type AppSection = { id: string; title: string; route: string };

/**
 * The app whose tab the side panel shows (open, at any size): the conversation's docked app the
 * sidebar's toggle follows. None while the panel is closed or shows a tool that is not an app.
 */
export function useDockedApp(): PluginPanel | null {
  const workbench = useWorkbench();
  const open = useDesktopShellStore((state) => state.sidePanelOpen);
  const view = useDesktopShellStore((state) => activePanelTab(state.panelTabs)?.view ?? null);
  if (!open || view === null) return null;
  return railPanels(workbench).find((panel) => panel.view === view) ?? null;
}

/** The tab of the docked app (its targets and live sections are its own). */
export function useDockedTabId(): string | null {
  return useDesktopShellStore((state) => (state.sidePanelOpen ? state.panelTabs.activeId : null));
}

/** The workspace an app works in: the open conversation's, once it exists (none outside the shell). */
function useAppWorkspace(): string | undefined {
  const store = useOptionalDesktopShellStoreApi();
  return useSyncExternalStore(
    (listener) => store?.subscribe(listener) ?? (() => undefined),
    () => {
      if (store === null) return undefined;
      const { chatId, syncedThreads } = store.getState();
      return syncedThreads.some((thread) => thread.id === chatId) ? chatId : undefined;
    },
    () => undefined,
  );
}

async function load(key: string, plugin: string, route: string, workspace?: string) {
  const sequence = (loads.get(key) ?? 0) + 1;
  loads.set(key, sequence);
  const previous = sidebars.get(key);
  publish(key, { ...(previous ?? EMPTY), loading: true });
  try {
    const result: SidebarItems = sidebarItemsSchema.parse(
      await callApp({
        plugin,
        route,
        method: 'GET',
        ...(workspace === undefined ? {} : { conversation: workspace }),
      }),
    );
    if (loads.get(key) === sequence) publish(key, { ...result, loading: false, error: null });
  } catch (cause) {
    if (loads.get(key) === sequence)
      publish(key, {
        items: previous?.items ?? [],
        loading: false,
        error: cause instanceof Error ? cause.message : String(cause),
      });
  }
}

/** Asks the sidebar sections of this app's tab to list their items again. */
export function refreshAppSidebar(view: string): void {
  window.dispatchEvent(new CustomEvent(REFRESH_EVENT, { detail: view }));
}

/** The items of one section of an app's navigation, loaded from its app and kept current. */
export function useAppSectionItems(
  panel: PluginPanel | null,
  section: AppSection | null,
  options: {
    /** Full screen: no conversation's workspace (`null`), whatever conversation is under it. */
    workspace?: string | null;
  } = {},
): SidebarState {
  const shown = useAppWorkspace();
  const workspace = options.workspace === undefined ? shown : (options.workspace ?? undefined);
  const key =
    panel === null || section === null
      ? null
      : JSON.stringify([panel.view, panel.version, section.id, section.route, workspace ?? null]);
  const state = useSyncExternalStore(
    subscribe,
    () => (key === null ? undefined : sidebars.get(key)),
    () => undefined,
  );
  // The key covers the panel, the section and the workspace: what the effect reads follows it.
  const view = panel?.view ?? null;
  const plugin = panel?.appView?.plugin;
  const route = section?.route ?? null;
  useEffect(() => {
    if (key === null || view === null || route === null || plugin === undefined) return;
    const reload = () => void load(key, plugin, route, workspace);
    const requested = (event: Event) => {
      if ((event as CustomEvent<string>).detail === view) reload();
    };
    reload();
    window.addEventListener(REFRESH_EVENT, requested);
    window.addEventListener('crowdex-plugins-changed', reload);
    return () => {
      window.removeEventListener(REFRESH_EVENT, requested);
      window.removeEventListener('crowdex-plugins-changed', reload);
    };
  }, [key, view, plugin, route, workspace]);
  return state ?? (key === null ? { items: [], loading: false, error: null } : EMPTY);
}

// --- live sections (`page.sections.set`) and what the user picks in them, per app tab --------

/** An app full screen keeps its live sections under this key, as a docked tab under its id. */
export function fullScreenSectionsKey(plugin: string): string {
  return `full:${plugin}`;
}

const live = new Map<string, readonly PageSection[]>();
const picked = new Map<string, { section: string; item: string }>();
const NO_SECTIONS: readonly PageSection[] = [];

function notify() {
  for (const listener of listeners) listener();
}

/** The live sections the view of tab `tabId` declares now (none clears them). */
export function setLiveSections(tabId: string, sections: readonly PageSection[]): void {
  if (sections.length === 0) live.delete(tabId);
  else live.set(tabId, sections);
  notify();
}

/** The live sections of tab `tabId`. */
export function useLiveSections(tabId: string | null): readonly PageSection[] {
  return useSyncExternalStore(
    subscribe,
    () => (tabId === null ? NO_SECTIONS : (live.get(tabId) ?? NO_SECTIONS)),
    () => NO_SECTIONS,
  );
}

/** The user picked `item` of the live section `section` of tab `tabId`: its view shows it. */
export function pickLiveItem(tabId: string, section: string, item: string): void {
  picked.set(tabId, { section, item });
  // Shown picked at once, until the view says what it shows.
  const sections = live.get(tabId);
  if (sections !== undefined)
    live.set(
      tabId,
      sections.map((entry) => (entry.id === section ? { ...entry, selected: item } : entry)),
    );
  notify();
}

/** What the user picked last in the live sections of tab `tabId` (`context.selection`). */
export function useLiveSelection(tabId: string): { section: string; item: string } | null {
  return useSyncExternalStore(
    subscribe,
    () => picked.get(tabId) ?? null,
    () => null,
  );
}

/** Forgets a closed tab's live sections. */
export function forgetLiveSections(tabId: string): void {
  if (!live.has(tabId) && !picked.has(tabId)) return;
  live.delete(tabId);
  picked.delete(tabId);
  notify();
}
